import {config} from "@/constants/config";
import type {TResponseRequest} from "@/interfaces/http.interface";
import httpRequest from "@/providers/http/request.http";
import {buildQueryUrl} from "@/providers/http/request.instance";
import {type PaginatedResult, normalizePagination} from "@/utils/pagination";

export interface PatternAlert {
  id: string;
  userId: string;
  patternType: string;
  riskLevel: string;
  detail: Record<string, unknown>;
  status: string;
  complianceAgentId: string | null;
  resolution: string | null;
  resolutionNotes: string | null;
  createdAt: string;
  resolvedAt: string | null;
}

export interface AccountFreeze {
  id: string;
  userId: string;
  freezeType: string;
  reason: string;
  trigger: string;
  agentId: string | null;
  startedAt: string;
  endsAt: string | null;
  fundsRetainedCents: string;
  fundsCurrency: string | null;
  fundsStatus: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SosReport {
  id: string;
  userId: string;
  triggerPatternIds: string[];
  totalSuspiciousCents: string;
  currency: string;
  autoDescription: string;
  complianceNotes: string | null;
  status: string;
  regulatorDestination: string | null;
  sentAt: string | null;
  agentId: string | null;
  createdAt: string;
  updatedAt: string;
}

export type AlertListResult = PaginatedResult<PatternAlert>;

export type FreezeListResult = PaginatedResult<AccountFreeze>;

export type SosReportListResult = PaginatedResult<SosReport>;

// ─── Pattern Alerts ───

interface AlertFilters {
  page?: number;
  limit?: number;
  status?: string;
  riskLevel?: string;
  patternType?: string;
  userId?: string;
}

export const getAlertsService = async (filters: AlertFilters = {}): Promise<TResponseRequest<AlertListResult>> => {
  const raw = await httpRequest.get<PatternAlert[]>({
    url: buildQueryUrl(`${config.apiBaseUrl}/bo/compliance/alerts`, filters as Record<string, unknown>),
  });
  if (!raw.data || !raw.pagination) return {...raw, data: undefined};
  return {...raw, data: {items: raw.data, pagination: normalizePagination(raw.pagination)}};
};

export const getAlertByIdService = async (id: string): Promise<TResponseRequest<PatternAlert>> => {
  return httpRequest.get<PatternAlert>({
    url: `${config.apiBaseUrl}/bo/compliance/alerts/${encodeURIComponent(id)}`,
  });
};

export const resolveAlertService = async (
  id: string,
  body: {status: string; resolution?: string; resolutionNotes?: string}
): Promise<TResponseRequest<PatternAlert>> => {
  return httpRequest.patch<PatternAlert>({
    url: `${config.apiBaseUrl}/bo/compliance/alerts/${encodeURIComponent(id)}/resolve`,
    body,
  });
};

// ─── Account Freezes ───

interface FreezeFilters {
  page?: number;
  limit?: number;
  userId?: string;
  freezeType?: string;
  isActive?: boolean;
}

export const getFreezesService = async (filters: FreezeFilters = {}): Promise<TResponseRequest<FreezeListResult>> => {
  const raw = await httpRequest.get<AccountFreeze[]>({
    url: buildQueryUrl(`${config.apiBaseUrl}/bo/compliance/freezes`, filters as Record<string, unknown>),
  });
  if (!raw.data || !raw.pagination) return {...raw, data: undefined};
  return {...raw, data: {items: raw.data, pagination: normalizePagination(raw.pagination)}};
};

export const getFreezeByIdService = async (id: string): Promise<TResponseRequest<AccountFreeze>> => {
  return httpRequest.get<AccountFreeze>({
    url: `${config.apiBaseUrl}/bo/compliance/freezes/${encodeURIComponent(id)}`,
  });
};

export const createFreezeService = async (body: {
  userId: string;
  freezeType: string;
  reason: string;
  trigger: string;
  endsAt?: string;
  fundsRetainedCents?: string;
  fundsCurrency?: string;
}): Promise<TResponseRequest<AccountFreeze>> => {
  return httpRequest.post<AccountFreeze>({
    url: `${config.apiBaseUrl}/bo/compliance/freezes`,
    body,
  });
};

export const resolveFreezeService = async (
  id: string,
  body: {resolution: string; fundsStatus?: string; notes?: string}
): Promise<TResponseRequest<AccountFreeze>> => {
  return httpRequest.patch<AccountFreeze>({
    url: `${config.apiBaseUrl}/bo/compliance/freezes/${encodeURIComponent(id)}/resolve`,
    body,
  });
};

// ─── SOS Reports ───

interface SosFilters {
  page?: number;
  limit?: number;
  userId?: string;
  status?: string;
}

export const getSosReportsService = async (
  filters: SosFilters = {}
): Promise<TResponseRequest<SosReportListResult>> => {
  const raw = await httpRequest.get<SosReport[]>({
    url: buildQueryUrl(`${config.apiBaseUrl}/bo/compliance/sos-reports`, filters as Record<string, unknown>),
  });
  if (!raw.data || !raw.pagination) return {...raw, data: undefined};
  return {...raw, data: {items: raw.data, pagination: normalizePagination(raw.pagination)}};
};

export const getSosReportByIdService = async (id: string): Promise<TResponseRequest<SosReport>> => {
  return httpRequest.get<SosReport>({
    url: `${config.apiBaseUrl}/bo/compliance/sos-reports/${encodeURIComponent(id)}`,
  });
};

export const createSosReportService = async (body: {
  userId: string;
  triggerPatternIds: string[];
  totalSuspiciousCents: string;
  currency: string;
  autoDescription: string;
}): Promise<TResponseRequest<SosReport>> => {
  return httpRequest.post<SosReport>({
    url: `${config.apiBaseUrl}/bo/compliance/sos-reports`,
    body,
  });
};

export const updateSosReportService = async (
  id: string,
  body: Partial<{
    complianceNotes: string;
    status: string;
    regulatorDestination: string;
    agentId: string;
  }>
): Promise<TResponseRequest<SosReport>> => {
  return httpRequest.patch<SosReport>({
    url: `${config.apiBaseUrl}/bo/compliance/sos-reports/${encodeURIComponent(id)}`,
    body,
  });
};
